import { useContext } from 'react'

import { Progress, Tooltip } from 'antd'
import { color } from 'd3-color'
import LiquidFillGauge from 'react-liquid-gauge'
import { useSelector } from 'react-redux'
import { faMeh, faSmile, faFrown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import Loading from '../shared/Loading'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { GlobalContext } from '../../Providers/GlobalProvider'
import { InstitutionOptions } from '../../utils/constants'
import BlurContent from '../shared/BlurContent'
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons'

const SmallScoreSummaryCard = ({ colorCircle, entityData, fillColor, gradientStops, loading, title }) => {
  const { modules } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)

  const isRecruitmentModuleEnabled = modules[InstitutionOptions.RECRUITMENT_MODULE]
  const usePlaceholder = !isRecruitmentModuleEnabled

  const percentLabel = value => (
    <span style={{ color: '#5b5b5b' }}>
      {value}
      <span style={{ fontSize: '14px' }}>%</span>
    </span>
  )

  const placeholderStats = {
    responseCount: 67,
    countAllEvaluation: 67,
    negativeEvaluation: 67,
    neutralEvaluation: 67,
    positiveEvaluation: 67,
    percentValue: 67,
    percentText: '67'
  }
  const institutionFeedbackSum = usePlaceholder ? placeholderStats.percentValue : entityData.institutionFeedbackSum
  const institutionResponseRate = usePlaceholder ? placeholderStats.percentValue : entityData.institutionResponseRate
  const formatPercent = usePlaceholder ? () => percentLabel(placeholderStats.percentText) : percent => percentLabel(percent)
  const institutionTextRenderer = usePlaceholder ? () => `${placeholderStats.percentValue}%` : undefined
  const responseCount = usePlaceholder ? placeholderStats.responseCount : entityData.responseCount
  const countAllEvaluation = usePlaceholder ? placeholderStats.countAllEvaluation : entityData.countAllEvaluation
  const negativeEvaluation = usePlaceholder ? placeholderStats.negativeEvaluation : entityData.negativeEvaluation
  const neutralEvaluation = usePlaceholder ? placeholderStats.neutralEvaluation : entityData.neutralEvaluation
  const positiveEvaluation = usePlaceholder ? placeholderStats.positiveEvaluation : entityData.positiveEvaluation

  return (
    <div className='score-summary-card-small'>
      <div className='title-score-summary-card'>{title}</div>
      {loading && <Loading size='4x' />}
      {!loading && (
        <div className='card-content'>
          <div className='charts-score-summary-card'>
            <div className='chart-score-summary-card'>
              <Progress
                type='circle'
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068'
                }}
                percent={entityData.feedbackSum}
                width={90}
                height={90}
                format={(percent) => (
                  <span style={{ color: '#5b5b5b' }}>
                    {percent}
                    <span style={{ fontSize: '14px' }}>%</span>
                  </span>
                )}
              />
              <p style={{ fontSize: '18px', marginLeft: '15px' }}>
                {t('score.card.score.feedbackSum.sector')}
                <span>
                  <Tooltip title={t('score.card.score.feedbackSum.sector.tooltip')}>
                    <FontAwesomeIcon icon={faQuestionCircle} size='xs' style={{ marginLeft: '5px' }} />
                  </Tooltip>
                </span>
              </p>
            </div>
            <div className='chart-score-summary-card'>
              <div style={{ height: 90, width: 90 }}>
                <LiquidFillGauge
                  style={{ margin: '0' }}
                  width={90}
                  height={90}
                  value={entityData.responseRate}
                  textSize={0.8}
                  textOffsetX={0}
                  textOffsetY={10}
                  waveAnimation
                  waveFrequency={2}
                  waveAmplitude={1}
                  gradient
                  gradientStops={gradientStops}
                  circleStyle={{
                    fill: colorCircle
                  }}
                  waveStyle={{
                    fill: fillColor
                  }}
                  textStyle={{
                    fill: color('#5b5b5b').toString()
                  }}
                  waveTextStyle={{
                    fill: color('#fff').toString(),
                    fontFamily: 'Arial'
                  }}
                />
              </div>
              <p
                style={{
                  fontSize: '18px',
                  marginLeft: '20px'
                }}
              >
                {t('score.card.score.responseRate')}
                <span>
                  <Tooltip title={t('score.card.score.responseRate.tooltip')}>
                    <FontAwesomeIcon
                      icon={faQuestionCircle}
                      size='xs'
                      style={{ marginLeft: '5px' }}
                    />
                  </Tooltip>
                </span>
              </p>
              <div className='evaluations-score-summary-card'>
                <BlurContent
                  blurIntensity='7px'
                  activated={!isRecruitmentModuleEnabled}
                >
                  <div style={{ marginBottom: '10px' }}>
                    {t('score.card.score.statistics.responseRatio', {
                      count: responseCount,
                      total: countAllEvaluation
                    })}
                  </div>
                  <div className='flex-row jc-between'>
                    <div>
                      <FontAwesomeIcon icon={faFrown} size='lg' color='#cf1322' />{' '}
                      <span>{negativeEvaluation}</span>
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faMeh} size='lg' color='#d46b08' />{' '}
                      <span>{neutralEvaluation}</span>
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faSmile} size='lg' color='#389e0d' />{' '}
                      <span>{positiveEvaluation}</span>
                    </div>
                  </div>
                </BlurContent>
              </div>
            </div>
          </div>
          <div className='charts-score-summary-card'>
            <BlurContent
              blurIntensity='7px'
              activated={!isRecruitmentModuleEnabled}
            >
              <div className='chart-score-summary-card'>
                <Progress
                  type='circle'
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068'
                  }}
                  width={90}
                  height={90}
                  percent={institutionFeedbackSum}
                  format={formatPercent}
                />
                <p
                  style={{
                    fontSize: '18px',
                    marginLeft: '15px'
                  }}
                >
                  {t('score.card.score.institutionFeedbackSum')}
                  <span>
                    <Tooltip
                      title={t('score.card.score.institutionFeedbackSum.tooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        size='xs'
                        style={{ marginLeft: '5px' }}
                      />
                    </Tooltip>
                  </span>
                </p>
              </div>
              <div className='chart-score-summary-card'>
                <div
                  style={{
                    height: 90,
                    width: 90
                  }}
                >
                  <LiquidFillGauge
                    style={{ margin: '0' }}
                    width={90}
                    height={90}
                    value={institutionResponseRate}
                    textSize={0.8}
                    textOffsetX={0}
                    textOffsetY={10}
                    waveAnimation
                    waveFrequency={2}
                    waveAmplitude={1}
                    gradient
                    gradientStops={gradientStops}
                    textRenderer={institutionTextRenderer}
                    circleStyle={{
                      fill: colorCircle
                    }}
                    waveStyle={{
                      fill: fillColor
                    }}
                    textStyle={{
                      fill: color('#5b5b5b').toString()
                    }}
                    waveTextStyle={{
                      fill: color('#fff').toString(),
                      fontFamily: 'Arial'
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: '18px',
                    marginLeft: '15px'
                  }}
                >
                  {t('score.card.score.responseRateInstitution')}
                  <span>
                    <Tooltip
                      title={t('score.card.score.responseRateInstitution.tooltip')}
                    >
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        size='xs'
                        style={{ marginLeft: '5px' }}
                      />
                    </Tooltip>
                  </span>
                </p>
              </div>
            </BlurContent>
          </div>
        </div>
      )}
    </div>
  )
}

export default SmallScoreSummaryCard
